/**
 * The course editor's own texts (ADR 0009 §1): one editor for HR's console and for the back
 * office, each of which has its own catalog and carries none of the other's (ADR 0008 §2). Both
 * entries import this one. Spanish is the reference catalog and follows the Figma (291:2579 and
 * 55:446); every other locale must provide exactly the same keys, which the compiler enforces.
 * ICU MessageFormat.
 */
export const es = {
  'load.loading': 'Abriendo el curso…',
  'load.error': 'No se ha podido abrir el curso. Vuelve a intentarlo en unos minutos.',
  'load.retry': 'Reintentar',
  'load.notFound': 'Este curso no existe o ya no está disponible.',
  'load.archived': 'Este curso está archivado y ya no se puede editar.',
  'load.importPending.catalog':
    'Este curso tiene una versión importada que todavía no se ha publicado. Publícala con la herramienta de operador antes de editarlo aquí.',
  'load.importPending.organization':
    'Este curso tiene una versión que Eklevo ha importado y todavía no ha publicado. Podrás editarlo cuando Eklevo la publique.',
  'load.notAllowed': 'No puedes editar este curso.',

  'create.title': 'Crear nuevo curso',
  'create.body': 'Se crea como borrador, sin lecciones: nadie lo ve hasta que se publica.',
  'create.name': 'Nombre del curso',
  'create.description': 'Descripción',
  'create.series.hint':
    'Opcional, con el nivel. Letras minúsculas, números y guiones, por ejemplo «liderazgo».',
  'create.submit': 'Crear y empezar a editar',
  'create.busy': 'Creando…',
  'create.cancel': 'Cancelar',
  'create.error': 'No se ha podido crear el curso. Vuelve a intentarlo en unos minutos.',
  'create.error.limit':
    'Tu organización ha llegado al máximo de cursos propios. Para crear otro, descarta un borrador que no necesites o escribe a hola@eklevo.com.',

  'page.breadcrumb': 'Ruta',
  'page.breadcrumb.edit': 'Editar',
  'page.untitled': 'Curso sin título',
  'page.status.draft': 'Borrador',
  'page.status.published': 'Publicado · versión {version, number}',

  'outline.title': 'Contenido del curso',
  'outline.empty': 'Todavía no hay ninguna lección.',
  'outline.lesson': 'Lección {number, number}: {title}',
  'outline.lesson.untitled': 'Lección {number, number}: Sin título',
  'outline.intermediate': 'Evaluación intermedia: {title}',
  'outline.intermediate.untitled': 'Evaluación intermedia',
  'outline.final': 'Evaluación final: {title}',
  'outline.final.untitled': 'Evaluación final',
  'outline.hasProblems': 'Tiene campos por revisar',
  'outline.add': 'Añadir lección',
  'outline.addIntermediate': 'Añadir evaluación intermedia tras la lección {number, number}',
  'outline.intermediateTaken':
    'La lección {number, number} ya tiene una evaluación intermedia detrás.',
  'outline.addFinal': 'Añadir evaluación final',
  'outline.finalBlocked':
    'La última lección ya tiene una evaluación intermedia detrás: muévela o elimínala para añadir la final.',
  'outline.full': 'Un curso tiene como máximo {max, number} lecciones.',
  'outline.drag': 'Arrastrar para mover',
  'outline.moveUp': 'Subir la lección {number, number}',
  'outline.moveDown': 'Bajar la lección {number, number}',
  'outline.remove': 'Eliminar la lección {number, number}',
  'outline.removeAssessment': 'Eliminar «{name}»',
  'outline.moved': '«{title}» se ha movido a la posición {number, number} de {count, number}.',
  'outline.moved.untitled':
    'La lección sin título se ha movido a la posición {number, number} de {count, number}.',

  'general.title': 'Información general',
  'general.courseTitle': 'Título del curso',
  'general.description': 'Descripción breve',
  'general.description.count': '{count, number}/{max, number} caracteres',
  'general.longDescription': 'Descripción del curso',
  'general.longDescription.hint':
    'Opcional. De qué trata el curso, con más detalle: se muestra en la página del curso, bajo la descripción breve.',
  'general.objectives': 'Objetivos del curso',
  'general.objectives.hint':
    'Opcional. Qué sabrá hacer la persona al terminar el curso. Se muestra en la página del curso.',
  'general.language': 'Idioma del curso',
  'general.language.newVersion':
    'Cambiar el idioma de un curso publicado crea una versión nueva al publicar: solo la reciben las asignaciones nuevas.',
  'general.series': 'Serie',
  'general.series.hint':
    'Opcional. Los cursos de una misma serie son sus niveles. Letras minúsculas, números y guiones, por ejemplo «liderazgo».',
  'general.level': 'Nivel',
  'general.level.none': 'Sin nivel',
  'general.level.option': 'Nivel {level, number}',
  'general.level.hint': 'Opcional. Va con la serie: los dos o ninguno.',
  'general.minutes': 'Tiempo estimado de finalización (minutos)',
  'general.department': 'Departamento',
  'general.department.none': 'Sin departamento',
  'general.department.hint': 'El departamento que lleva este programa. Se guarda al elegirlo.',
  'general.department.saving': 'Guardando el departamento…',
  'general.department.error': 'No se ha podido guardar el departamento. Vuelve a elegirlo.',

  'number.invalid': 'Escribe un número entero entre {min, number} y {max, number}.',
  'format.hint': 'Puedes usar **negrita**, *cursiva* y listas con «-».',

  'language.es': 'Español',
  'language.ca': 'Catalán',
  'language.en': 'Inglés',

  'lesson.heading': 'Lección {number, number}',
  'lesson.none.title': 'Este curso todavía no tiene lecciones',
  'lesson.none.body': 'Añade la primera lección para empezar a escribir su contenido.',
  'lesson.title': 'Título de la lección',
  'lesson.description': 'Descripción de la lección',
  'lesson.objectives': 'Objetivos de la lección',
  'lesson.objectives.hint': 'Opcional. Qué se aprende en esta lección.',
  'lesson.keyIdeas': 'Ideas clave',
  'lesson.keyIdeas.hint':
    'Opcional. Solo las lee Noa, en las empresas que la tienen activada: la persona no las ve.',
  'lesson.optional': 'Opcional.',
  'lesson.minutes': 'Duración (minutos)',
  'lesson.remove': 'Eliminar lección',
  'lesson.assessment.title': 'Evaluación intermedia',
  'lesson.assessment.none':
    'Una evaluación intermedia es una prueba con nota que va justo después de esta lección: para pasar a la siguiente, hay que superarla. Tiene sus propias preguntas, que no se toman del test de la lección.',
  'lesson.assessment.add': 'Añadir evaluación intermedia',
  'lesson.assessment.has': 'Después de esta lección va «{title}».',
  'lesson.assessment.hasUntitled':
    'Después de esta lección va una evaluación intermedia sin título.',
  'lesson.assessment.open': 'Editar la evaluación',
  'lesson.assessment.last': 'Después de la última lección va la evaluación final.',

  'video.title': 'Vídeo',
  'video.source': 'Origen del vídeo',
  'video.source.file': 'Subir archivo',
  'video.source.external': 'Enlace externo',
  'video.upload.formats': 'Formatos aceptados: MP4 (H.264). Tamaño máximo: 500 MB.',
  'video.upload.choose': 'Elegir vídeo',
  'video.file.waiting': 'Vídeo sin archivo: la subida no terminó.',
  'video.file.again': 'Subir el vídeo de nuevo',
  'video.file': 'Vídeo subido',
  'video.file.captions': 'Con subtítulos.',
  'video.file.noCaptions': 'Sin subtítulos.',
  'captions.title': 'Subtítulos',
  'captions.formats': 'Formato WebVTT (.vtt), hasta 2 MB. Opcional.',
  'captions.choose': 'Añadir subtítulos',
  'captions.file': 'Subtítulos subidos.',
  'captions.waiting': 'Subtítulos sin archivo: la subida no terminó.',
  'captions.again': 'Subir los subtítulos de nuevo',
  'captions.remove': 'Quitar subtítulos',
  'video.file.replace':
    'Para cambiarlo por otro vídeo o por un enlace de YouTube o Vimeo, quita primero el vídeo subido.',
  'video.remove': 'Quitar vídeo',
  'video.newVersion':
    'Añadir un vídeo a esta lección o quitárselo cambia la estructura del curso: al publicar, será una versión nueva. Cambiar el enlace, el archivo o los subtítulos de un vídeo es una corrección.',
  'video.link': 'Enlace de YouTube o Vimeo',
  'video.link.placeholder': 'https://www.youtube.com/watch?v=…',
  'video.link.youtube': 'Vídeo de YouTube.',
  'video.link.vimeo': 'Vídeo de Vimeo.',
  'video.link.unknown':
    'Este enlace no es de YouTube ni de Vimeo: solo se pueden publicar vídeos de YouTube o Vimeo, con https.',
  'practice.title': 'Práctica',
  'practice.body':
    'Hasta {max, number} actividades para poner en práctica lo que se ha visto. En la lección, van después del vídeo. No cuentan para completarla.',
  'practice.empty': 'Esta lección todavía no tiene práctica.',
  'practice.activity': 'Actividad {number, number}',
  'practice.kind': 'Tipo',
  'practice.kind.identification': 'Identificación',
  'practice.kind.case': 'Caso práctico',
  'practice.kind.noa': 'Practicar con Noa',
  'practice.kind.other': 'Otra',
  'practice.kind.noa.hint':
    'En el portal, esta actividad solo aparece en las empresas que tienen Noa activada, con el botón «Practicar con Noa».',
  'practice.name': 'Título de la actividad',
  'practice.instructions': 'Instrucciones',
  'practice.instructions.hint': 'Qué tiene que hacer la persona.',
  'practice.minutes': 'Duración de la actividad (minutos)',
  'practice.scenario': 'Escenario para Noa',
  'practice.scenario.hint':
    'Opcional. Solo lo lee Noa, cuando la persona practica con ella: el papel que hace Noa y lo que quiere practicar la persona. La persona no lo ve.',
  'practice.scenario.onlyNoa':
    'Solo las actividades de «Practicar con Noa» tienen escenario: cambia el tipo o borra el escenario para poder publicar.',
  'practice.add': 'Añadir actividad',
  'practice.full': 'Una lección tiene como máximo {max, number} actividades.',
  'practice.moveUp': 'Subir la actividad {number, number}',
  'practice.moveDown': 'Bajar la actividad {number, number}',
  'practice.remove': 'Eliminar la actividad {number, number}',
  'practice.moved': '«{title}» se ha movido a la posición {number, number} de {count, number}.',
  'practice.moved.untitled':
    'La actividad sin título se ha movido a la posición {number, number} de {count, number}.',

  'materials.title': 'Materiales',
  'materials.empty': 'Esta lección todavía no tiene materiales.',
  'materials.pdf': 'PDF',
  'materials.link': 'Link',
  'materials.number': 'Material {number, number}',
  'materials.name': 'Nombre',
  'materials.url': 'Enlace (https)',
  'materials.description': 'Descripción',
  'materials.description.hint':
    'Opcional. Qué encontrará la persona en este material. Se muestra bajo su nombre.',
  'materials.file': 'Archivo subido.',
  'materials.file.waiting': 'Sin archivo: la subida no terminó.',
  'materials.file.again': 'Subir el PDF de nuevo',
  'materials.formats': 'PDF de hasta 50 MB.',
  'materials.addLink': 'Añadir link externo',
  'materials.uploadPdf': 'Subir PDF',
  'materials.full': 'Una lección tiene como máximo {max, number} materiales.',
  'materials.remove': 'Eliminar el material {number, number}',
  'upload.sending': 'Subiendo {name}',
  'upload.percent': '{percent, number, percent}',
  'upload.progress': 'Progreso de la subida',
  'upload.scanning': 'Analizando {name}: revisamos que el archivo sea seguro.',
  'upload.cancel': 'Cancelar',
  'upload.cancel.label': 'Cancelar la subida de {name}',
  'upload.error.too-large': 'El archivo supera el tamaño máximo.',
  'upload.error.empty': 'El archivo está vacío.',
  'upload.error.wrong-type': 'El archivo no es del formato que se acepta aquí.',
  'upload.error.quota-exceeded':
    'Este mes ya no se pueden subir más archivos: la organización ha llegado al límite de 20 GB.',
  'upload.error.infected':
    'La revisión de seguridad ha encontrado un problema en el archivo, y no se ha subido.',
  'upload.error.scan-failed': 'No se ha podido revisar el archivo. Prueba con otro.',
  'upload.error.missing': 'El archivo no ha llegado entero. Vuelve a subirlo.',
  'upload.error.expired': 'La subida ha caducado. Vuelve a subir el archivo.',
  'upload.error.network': 'Se ha perdido la conexión mientras se subía. Vuelve a subirlo.',
  'upload.error.scan-timeout':
    'La revisión del archivo está tardando demasiado. Vuelve a subirlo más tarde.',
  'upload.error.failed': 'No se ha podido subir el archivo. Vuelve a intentarlo.',

  'reflection.title': 'Ejercicio de reflexión',
  'reflection.body':
    'Hasta {max, number} momentos: preguntas abiertas que la persona responde por escrito. En la lección, va después del vídeo y de la práctica.',
  'reflection.intro': 'Introducción',
  'reflection.intro.hint':
    'Opcional. Se muestra antes de los momentos: para qué sirve el ejercicio o cómo hacerlo.',
  'reflection.intro.unseen':
    'Esta introducción no se verá: sin momentos, la lección no muestra el ejercicio de reflexión.',
  'reflection.empty': 'Esta lección todavía no tiene ejercicio de reflexión.',
  'reflection.moment': 'Momento {number, number}',
  'reflection.fixed':
    'Esta lección ya se ha publicado: puedes corregir el texto de cada momento, pero no cambiar su orden, añadir ni quitar momentos. Las respuestas de cada persona se guardan por su posición.',
  'reflection.add': 'Añadir momento',
  'reflection.full': 'Una lección tiene como máximo {max, number} momentos.',
  'reflection.moveUp': 'Subir el momento {number, number}',
  'reflection.moveDown': 'Bajar el momento {number, number}',
  'reflection.remove': 'Eliminar el momento {number, number}',
  'reflection.moved': '«{prompt}» se ha movido a la posición {number, number} de {count, number}.',
  'reflection.moved.untitled':
    'El momento sin texto se ha movido a la posición {number, number} de {count, number}.',

  'test.title': 'Test',
  'test.body':
    'Preguntas de repaso de la lección, cada una con cuatro opciones y una correcta. No tiene nota mínima, pero hay que enviarlo para completar la lección: al enviarlo, la persona ve las respuestas correctas y las explicaciones, y puede repetirlo. Si quieres una prueba que haya que superar, añade una evaluación intermedia después de la lección.',
  'test.empty': 'Esta lección todavía no tiene test.',
  'test.question': 'Pregunta {number, number}',
  'test.prompt': 'Enunciado',
  'test.options': 'Opciones',
  'test.options.hint': 'Marca con el círculo la opción correcta.',
  'test.option': 'Opción {number, number}',
  'test.correct': 'Correcta',
  'test.correct.label': 'La opción {number, number} es la correcta',
  'test.correct.none': 'Marca cuál es la opción correcta.',
  'test.correct.pending':
    'Todavía no has marcado la opción correcta. Puedes seguir, pero hará falta para publicar.',
  'test.correct.pending.announce':
    'Pregunta {number, number}: todavía no tiene marcada la opción correcta.',
  'test.explanation': 'Explicación',
  'test.explanation.hint': 'Opcional. Se muestra después de responder.',
  'test.add': 'Añadir pregunta',
  'test.full': 'Un test tiene como máximo {max, number} preguntas.',
  'test.remove': 'Eliminar la pregunta {number, number}',

  'assessment.intermediate': 'Evaluación intermedia',
  'assessment.intermediate.body':
    'Una prueba con nota justo después de la lección que elijas en «Va después de»: para seguir con el curso, hay que superarla. Tiene sus propias preguntas, que no se toman del test de la lección.',
  'assessment.final': 'Evaluación final',
  'assessment.final.body':
    'Una prueba con nota después de la última lección: hay que superarla para completar el curso. Tiene sus propias preguntas, que no se toman de los tests de las lecciones.',
  'assessment.remove': 'Eliminar evaluación',
  'assessment.title': 'Título de la evaluación',
  'assessment.title.hint': 'Por ejemplo, «Repaso de las lecciones 1 a 3».',
  'assessment.minutes': 'Tiempo estimado (minutos)',
  'assessment.after': 'Va después de',
  'assessment.after.hint':
    'Una sola evaluación detrás de cada lección, y ninguna detrás de la última si hay evaluación final.',
  'assessment.after.newVersion':
    'Moverla a otra lección cambia la estructura del curso: al publicar, será una versión nueva.',
  'assessment.after.taken': '{lesson} (ya tiene una evaluación)',
  'assessment.after.last': '{lesson} (detrás va la evaluación final)',
  'assessment.after.unknown': 'Elige una lección',
  'assessment.rules': 'Para aprobar',
  'assessment.threshold': 'Nota mínima',
  'assessment.threshold.value': '{value, number} % de aciertos',
  'assessment.attempts': 'Intentos',
  'assessment.attempts.value': '{value, plural, one {# intento} other {# intentos}}',
  'assessment.rules.hint': 'Son las mismas en todas las evaluaciones y no se cambian en el editor.',
  'assessment.questions': 'Preguntas de la evaluación',
  'assessment.questions.body':
    'Cada una con cuatro opciones y una correcta. Quien hace el curso nunca ve las respuestas correctas de una evaluación.',
  'assessment.questions.empty': 'Esta evaluación todavía no tiene preguntas.',
  'assessment.questions.required':
    'Añade al menos una pregunta: sin preguntas, la evaluación no se puede publicar.',
  'assessment.questions.full': 'Una evaluación tiene como máximo {max, number} preguntas.',

  'save.cancel': 'Cancelar',
  'save.now': 'Guardar cambios',
  'save.state.saved': 'Todos los cambios están guardados.',
  'save.state.savedAt': 'Cambios guardados a las {at, time, short}.',
  'save.state.waiting': 'Hay cambios sin guardar. Se guardan solos en unos segundos.',
  'save.state.saving': 'Guardando…',
  'save.state.invalid': 'No se han podido guardar los cambios: revisa los campos marcados.',
  'save.state.tooLarge':
    'El curso es demasiado grande para guardarlo. Acorta textos o quita lecciones o preguntas, y se guardará solo.',
  'save.state.retrying':
    'No se han podido guardar los cambios. Lo volveremos a intentar en unos segundos.',
  'save.state.stopped': 'Los cambios ya no se pueden guardar.',

  'save.conflict': 'Tu guardado ha sustituido cambios de otra persona.',
  'save.conflict.show': 'Ver el aviso',

  'stopped.gone':
    'Este borrador ya no existe: se ha publicado o descartado desde otra pestaña. Vuelve a abrir el curso para seguir editándolo.',
  'stopped.notFound': 'Este curso ya no existe o ya no tienes acceso a él.',
  'stopped.archived': 'Este curso se ha archivado y ya no se puede editar.',
  'stopped.notAllowed': 'No puedes editar este curso.',
  'stopped.error': 'No se han podido guardar los cambios.',
  'stopped.reopen': 'Volver a abrir el curso',
  'reopen.title': '¿Volver a abrir el curso?',
  'reopen.body':
    'Se perderán los cambios que no se han guardado: el curso se abre como está guardado ahora.',
  'reopen.confirm': 'Volver a abrir',

  'conflict.body':
    'Mientras editabas, alguien más guardó cambios en este curso, a las {at, time, short}. Tu último guardado los ha sustituido: revisa que no falte nada.',
  'conflict.dismiss': 'Entendido',

  'issues.title':
    '{count, plural, one {Hay un campo por revisar} other {Hay # campos por revisar}}',
  'issues.body': 'El borrador no se ha guardado con estos valores. Corrígelos y se guardará solo.',
  'issues.at': '{place}: {problem}',
  'issues.course': 'Curso',

  'place.course.title': 'Título del curso',
  'place.course.description': 'Descripción breve',
  'place.course.longDescription': 'Descripción del curso',
  'place.course.objectives': 'Objetivos del curso',
  'place.course.language': 'Idioma del curso',
  'place.course.series': 'Serie',
  'place.course.level': 'Nivel',
  'place.course.estimatedMinutes': 'Tiempo estimado de finalización',
  'place.course.lessons': 'Lecciones',
  'place.lesson': 'Lección {number, number}',
  'place.intermediate': 'Evaluación intermedia',
  'place.final': 'Evaluación final',
  'place.intermediate.after': 'Evaluación tras la lección {number, number}',
  'place.estimatedMinutes': 'Tiempo estimado',
  'place.afterLessonKey': 'Va después de',
  'place.passThreshold': 'Nota mínima',
  'place.maxAttempts': 'Intentos',
  'place.title': 'Título',
  'place.description': 'Descripción',
  'place.objectives': 'Objetivos',
  'place.keyIdeas': 'Ideas clave',
  'place.durationMinutes': 'Duración',
  'place.video': 'Vídeo',
  'place.materials': 'Materiales',
  'place.material': 'Material {number, number}',
  'place.name': 'Nombre',
  'place.url': 'Enlace',
  'place.reflection': 'Ejercicio de reflexión',
  'place.reflectionIntro': 'Introducción de la reflexión',
  'place.practice': 'Práctica',
  'place.activity': 'Actividad {number, number}',
  'place.kind': 'Tipo',
  'place.instructions': 'Instrucciones',
  'place.noaScenario': 'Escenario para Noa',
  'place.moment': 'Momento {number, number}',
  'place.test': 'Test',
  'place.questions': 'Preguntas',
  'place.question': 'Pregunta {number, number}',
  'place.prompt': 'Enunciado',
  'place.options': 'Opciones',
  'place.option': 'Opción {number, number}',
  'place.correct': 'Respuesta correcta',
  'place.explanation': 'Explicación',
  'place.separator': ' · ',

  'problem.required': 'Falta este dato.',
  'problem.too-long': 'Es demasiado largo.',
  'problem.too-few': 'Faltan elementos.',
  'problem.too-many': 'Hay demasiados elementos.',
  'problem.out-of-range': 'El valor está fuera del rango permitido.',
  'problem.invalid': 'El valor no es válido.',
  'problem.invalid-characters': 'Tiene caracteres que no se pueden guardar.',
  'problem.not-https': 'El enlace tiene que empezar por https://.',
  'problem.video-host': 'Solo se pueden publicar vídeos de YouTube o Vimeo.',
  'problem.only-for-noa': 'Solo las prácticas con Noa tienen escenario.',
  'problem.duplicate-key': 'Este elemento está repetido.',
  'problem.unknown-lesson': 'La lección de la que va detrás ya no existe.',
  'problem.lesson-taken': 'Ya hay otra evaluación detrás de esta lección.',
  'problem.after-last-lesson':
    'No puede ir detrás de la última lección: ahí va la evaluación final.',
  'problem.series-and-level': 'La serie y el nivel van juntos: pon los dos o ninguno.',
  'problem.video-or-material-required': 'Alguna lección tiene que tener un vídeo o un material.',
  'problem.file-not-clean': 'El archivo todavía no se ha revisado o no ha pasado la revisión.',
  'problem.file-wrong-kind': 'El archivo no es del tipo que se espera aquí.',
  'problem.file-not-found': 'El archivo ya no está disponible.',
  'problem.reflection-fixed':
    'Esta lección ya se ha publicado: sus momentos no se pueden reordenar, añadir ni quitar.',
  'problem.level-taken': 'Otro curso de la serie ya tiene este nivel.',

  'actions.preview': 'Previsualizar como empleado',
  'actions.preview.busy': 'Abriendo la vista previa…',
  'actions.publish': 'Publicar',
  'actions.publish.checking': 'Comprobando…',
  'actions.publish.busy': 'Publicando…',
  'actions.discard': 'Descartar borrador',
  'preview.title': 'Vista previa',
  'preview.body':
    'Así verá cada lección una persona que hace el curso. Lo que respondas aquí no se guarda.',
  'preview.marked':
    'Has previsualizado esta versión del borrador: si no cambias nada, ya se puede publicar.',
  'preview.lessons': 'Lecciones',
  'preview.none': 'Este curso todavía no tiene lecciones.',
  'preview.untitled': 'Lección sin título',
  'preview.video.file':
    'El vídeo subido se ve en el portal; la vista previa todavía no lo reproduce.',
  'preview.reflection': 'En la vista previa, las respuestas no se guardan.',
  'preview.materials.pdf': 'Los PDF se descargan en el portal.',
  'preview.noa':
    'En el portal, esta actividad solo aparece en las empresas que tienen Noa activada; allí se practica con Noa.',
  'preview.back': 'Volver al editor',
  'publishPreview.title': 'Antes de publicar, previsualiza el curso',
  'publishPreview.body':
    'Los cursos del catálogo se publican después de ver cómo los verá una persona. Ábrelo en la vista previa y publícalo desde allí.',
  'publishPreview.confirm': 'Previsualizar como empleado',
  'publish.created.title': '¿Publicar el curso?',
  'publish.created.body.catalog':
    'Será la versión 1. Quedará disponible para activarlo en las empresas.',
  'publish.created.body.organization': 'Será la versión 1. Quedará disponible para asignarlo.',
  'publish.newVersion.title': '¿Publicar una versión nueva?',
  'publish.newVersion.body':
    'La estructura del curso ha cambiado (sus lecciones, cuáles tienen vídeo, sus preguntas, evaluaciones o su idioma): será la versión {version, number}. Solo la reciben las asignaciones nuevas; quien ya tiene el curso asignado sigue con la versión que tenía.',
  'publish.newVersion.confirm': 'Publicar versión nueva',
  'publish.correction.title': '¿Publicar una corrección?',
  'publish.correction.body':
    'La estructura del curso no cambia: es una corrección de la versión {version, number}. Llega a todas las personas que ya tienen el curso, sin cambiar los intentos que ya han hecho.',
  'publish.correction.confirm': 'Publicar corrección',
  'publish.confirm': 'Publicar',
  'publish.catalog': 'Vale para todas las empresas que tienen el curso.',
  'publish.passkey': 'Te pediremos tu clave de acceso para confirmarlo.',
  'publish.error.save':
    'Para publicar hay que guardar antes los cambios, y no se han podido guardar. Revisa el aviso del pie y vuelve a intentarlo.',
  'publish.error.changed':
    'Alguien ha guardado cambios en este curso mientras tanto. Vuelve a abrir el curso para verlos.',
  'publish.error.changedDuringCheck':
    'El curso ha cambiado mientras se comprobaba. No se ha publicado: vuelve a pulsar «Publicar».',
  'publish.error.gone':
    'Este borrador ya no existe: se ha publicado o descartado desde otra pestaña. Vuelve a abrir el curso para seguir editándolo.',
  'publish.error.generic': 'No se ha podido publicar. Vuelve a intentarlo en unos minutos.',
  'publish.error.limit':
    'Hoy tu organización ya ha publicado el máximo de versiones nuevas de sus cursos. Los cambios quedan guardados en el borrador: vuelve a intentarlo mañana.',
  'preview.error': 'No se ha podido abrir la vista previa. Vuelve a intentarlo en unos minutos.',
  'discard.error': 'No se ha podido descartar el borrador. Vuelve a intentarlo en unos minutos.',
  'issues.publish.body':
    'El borrador está guardado, pero todavía no se puede publicar: corrige estos campos y vuelve a publicar.',
  'discard.title': '¿Descartar el borrador?',
  'discard.body.new': 'Este curso no se ha publicado nunca: se eliminará con todo su contenido.',
  'discard.body.published':
    'Se perderán los cambios que no se han publicado. El curso seguirá como está publicado (versión {version, number}).',
  'discard.confirm': 'Descartar borrador',
  'discard.busy': 'Descartando…',
  'done.published.created.catalog':
    'Curso publicado: versión 1. Ya se puede activar en las empresas.',
  'done.published.created.organization': 'Curso publicado: versión 1. Ya se puede asignar.',
  'done.published.newVersion':
    'Versión {version, number} publicada. Solo la reciben las asignaciones nuevas.',
  'done.published.correction':
    'Corrección publicada en la versión {version, number}: ya la ve todo el mundo que tiene el curso.',
  'done.discarded.deleted':
    'Borrador descartado. El curso no se había publicado nunca y se ha eliminado.',
  'done.discarded.kept':
    'Borrador descartado. El curso sigue como está publicado (versión {version, number}).',
  'done.back': 'Volver a los cursos',
  'done.back.organization': 'Volver a los programas',
  'done.edit': 'Seguir editando',

  'leave.title': '¿Salir sin guardar?',
  'leave.body': 'Hay cambios que todavía no se han guardado. Si sales ahora, se perderán.',
  'leave.stay': 'Seguir editando',
  'leave.confirm': 'Salir sin guardar',

  'confirm.cancel': 'Cancelar',
  'confirm.lesson.title': '¿Eliminar la lección {number, number}?',
  'confirm.lesson.body':
    'Se eliminará «{title}» con su vídeo, la práctica, la reflexión, sus materiales y el test.',
  'confirm.lesson.bodyUntitled':
    'Se eliminará esta lección con su vídeo, la práctica, la reflexión, sus materiales y el test.',
  'confirm.lesson.assessments':
    '{count, plural, one {También se eliminará la evaluación que va detrás de ella.} other {También se eliminarán las # evaluaciones que van detrás de ella.}}',
  'confirm.lesson.published':
    'Quien ya tiene el curso asignado la conserva: el cambio solo llega a las asignaciones nuevas cuando publiques.',
  'confirm.lesson.confirm': 'Eliminar lección',
  'confirm.assessment.title': '¿Eliminar la evaluación?',
  'confirm.assessment.body': 'Se eliminará «{title}» con sus preguntas.',
  'confirm.assessment.bodyUntitled': 'Se eliminará esta evaluación con sus preguntas.',
  'confirm.assessment.confirm': 'Eliminar evaluación',
  'confirm.question.title': '¿Eliminar la pregunta {number, number}?',
  'confirm.question.body': 'Se eliminará la pregunta con sus opciones y su explicación.',
  'confirm.question.confirm': 'Eliminar pregunta',
  'confirm.material.title': '¿Eliminar el material {number, number}?',
  'confirm.material.body': 'Se quitará de la lección.',
  'confirm.material.confirm': 'Eliminar material',
  'confirm.moment.title': '¿Eliminar el momento {number, number}?',
  'confirm.moment.body': 'Se eliminará la pregunta de este momento.',
  'confirm.moment.confirm': 'Eliminar momento',
  'confirm.practice.title': '¿Eliminar la actividad {number, number}?',
  'confirm.practice.body': 'Se eliminará la actividad de la práctica de esta lección.',
  'confirm.practice.confirm': 'Eliminar actividad',
  'confirm.video.title': '¿Quitar el vídeo?',
  'confirm.video.body':
    'Se quitará el vídeo subido de esta lección, con sus subtítulos. Después podrás subir otro o añadir un enlace de YouTube o Vimeo.',
  'confirm.video.confirm': 'Quitar vídeo',
  /** The title of a duplicate of a course, in the course's language (ADR 0009 §8). */
  'course.duplicateTitle': 'Copia de {title}',
} as const

export type EditorMessageKey = keyof typeof es
export type EditorCatalog = Record<EditorMessageKey, string>
